/**
 * useFeedbackPrompts · Feb 2026
 * ───────────────────────────────────────────────────────────────────
 * The orchestration brain for Soulumo's beta feedback system.
 *
 * Mounted once at the top of MyJourneysPage (and anywhere else that
 * dispatches `soulumo:installation-ended` events). The hook:
 *   1. Listens for an installation ending.
 *   2. Waits 5 seconds — the "emotional landing" window Michael
 *      specified. We never interrupt that silence.
 *   3. Asks the backend `/beta-feedback/next-prompt` what — if
 *      anything — to show, forwarding full context.
 *   4. Dispatches the right modal via a global custom event the
 *      page listens for (`soulumo:feedback-show`), with payload.
 *   5. Logs the outcome via `/beta-feedback/prompt-log`.
 *
 * The backend owns ALL frequency rules — the hook has no logic of
 * its own beyond the 5-second wait. That keeps rule changes server-
 * side only.
 */
import { useEffect, useRef } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const LANDING_PAUSE_MS = 5000;

function auth() {
  const t = localStorage.getItem("reclaim_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function logOutcome(prompt_type, outcome, extra = {}) {
  try {
    await fetch(`${API}/api/beta-feedback/prompt-log`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth() },
      body: JSON.stringify({ prompt_type, outcome, ...extra }),
    });
  } catch (_) { /* non-fatal */ }
}

export default function useFeedbackPrompts() {
  const activeRef = useRef(null); // guard against double-fire

  useEffect(() => {
    const onEnded = (e) => {
      const ctx = (e && e.detail) || {};
      if (!localStorage.getItem("reclaim_token")) return; // anonymous: nothing
      const stamp = Date.now();
      activeRef.current = stamp;
      setTimeout(async () => {
        if (activeRef.current !== stamp) return; // superseded by newer event
        try {
          const r = await fetch(`${API}/api/beta-feedback/next-prompt`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...auth() },
            body: JSON.stringify({
              installation_id: ctx.journeyId || ctx.installationId || null,
              installation_name: ctx.installationName || null,
              category: ctx.category || null,
              experience_type: ctx.experienceType || null,
              duration_sec: ctx.durationSec || null,
              listened_sec: ctx.listenedSec || null,
              completion_pct: ctx.completionPct || null,
              voice_id: ctx.voiceId || null,
              voice_label: ctx.voiceLabel || null,
              own_voice: ctx.ownVoice || null,
              music: ctx.music || null,
              day_or_night: ctx.dayOrNight || null,
              device: window.innerWidth < 768 ? "mobile" : "desktop",
              trigger_source: "post-install",
              paused: ctx.paused || null,
              skipped: ctx.skipped || null,
              exited_early: ctx.exitedEarly || null,
            }),
          });
          if (!r.ok) return;
          const d = await r.json();
          if (!d.prompt) return;
          window.dispatchEvent(new CustomEvent("soulumo:feedback-show", {
            detail: {
              prompt: d.prompt,
              sessionNumber: d.session_number,
              isFirstSession: d.is_first_session,
              microPrompt: d.micro_prompt || null,
              context: ctx,
            },
          }));
          logOutcome(d.prompt, "shown", {
            installation_id: ctx.journeyId || null,
            session_number: d.session_number,
          });
        } catch (_) { /* offline */ }
      }, LANDING_PAUSE_MS);
    };
    window.addEventListener("soulumo:installation-ended", onEnded);
    return () => {
      activeRef.current = null;
      window.removeEventListener("soulumo:installation-ended", onEnded);
    };
  }, []);
}

export { logOutcome };
